'use client';

import { ChevronRight, MoreHorizontal } from 'lucide-react';
import { Slot as SlotPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/utils';

export type BreadcrumbMapping = {
    id: string;
    name: string;
    skipParentSegment?: boolean;
};

type BreadcrumbContextProps = {
    idNameMappings: BreadcrumbMapping[];
    addIdNameMapping: (mapping: BreadcrumbMapping) => void;
};

const BreadcrumbContext = React.createContext<BreadcrumbContextProps | null>(
    null,
);

function useBreadcrumb() {
    const context = React.useContext(BreadcrumbContext);
    if (!context) {
        throw new Error(
            'useBreadcrumb must be used within a BreadcrumbProvider.',
        );
    }

    return context;
}

function BreadcrumbProvider({
    children,
    ...props
}: React.ComponentProps<'div'>) {
    const [idNameMappings, setIdNameMappings] = React.useState<
        BreadcrumbMapping[]
    >([]);

    const addIdNameMapping = React.useCallback((mapping: BreadcrumbMapping) => {
        setIdNameMappings((prev) => [...prev, mapping]);
    }, []);

    const contextValue = React.useMemo<BreadcrumbContextProps>(
        () => ({
            idNameMappings,
            addIdNameMapping,
        }),
        [idNameMappings, addIdNameMapping],
    );

    return (
        <BreadcrumbContext.Provider value={contextValue} {...props}>
            {children}
        </BreadcrumbContext.Provider>
    );
}

function Breadcrumb({ ...props }: React.ComponentProps<'nav'>) {
    return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />;
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<'ol'>) {
    return (
        <ol
            data-slot="breadcrumb-list"
            className={cn(
                'text-muted-foreground flex min-w-0 flex-nowrap items-center gap-1.5 overflow-hidden text-sm whitespace-nowrap sm:gap-2.5',
                className,
            )}
            {...props}
        />
    );
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<'li'>) {
    return (
        <li
            data-slot="breadcrumb-item"
            className={cn(
                'inline-flex min-w-0 shrink items-center gap-1.5 text-sm',
                className,
            )}
            {...props}
        />
    );
}

function BreadcrumbLink({
    asChild,
    className,
    ...props
}: React.ComponentProps<'a'> & {
    asChild?: boolean;
}) {
    const Comp = asChild ? SlotPrimitive.Slot : 'a';

    return (
        <Comp
            data-slot="breadcrumb-link"
            className={cn(
                'hover:text-foreground min-w-0 transition-colors',
                className,
            )}
            {...props}
        />
    );
}

function BreadcrumbPage({ className, ...props }: React.ComponentProps<'span'>) {
    return (
        <span
            data-slot="breadcrumb-page"
            role="link"
            aria-disabled="true"
            aria-current="page"
            className={cn('text-foreground min-w-0 font-normal', className)}
            {...props}
        />
    );
}

function BreadcrumbSeparator({
    children,
    className,
    ...props
}: React.ComponentProps<'li'>) {
    return (
        <li
            data-slot="breadcrumb-separator"
            role="presentation"
            aria-hidden="true"
            className={cn('[&>svg]:size-3.5', className)}
            {...props}
        >
            {children ?? <ChevronRight />}
        </li>
    );
}

function BreadcrumbEllipsis({
    className,
    ...props
}: React.ComponentProps<'span'>) {
    return (
        <span
            data-slot="breadcrumb-ellipsis"
            role="presentation"
            aria-hidden="true"
            className={cn('flex size-9 items-center justify-center', className)}
            {...props}
        >
            <MoreHorizontal className="size-4" />
            <span className="sr-only">More</span>
        </span>
    );
}

export {
    Breadcrumb,
    BreadcrumbEllipsis,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbProvider,
    BreadcrumbSeparator,
    useBreadcrumb,
};
